import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Download, Clock, Loader2, XCircle } from "lucide-react";

export default function StoreSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/store/orders/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setOrder(data); setState("paid"); return; }
        attempts.current += 1;
        if (attempts.current >= 8) { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (active) setState("failed");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-lg px-4 sm:px-6 py-20 text-center" data-testid="store-success-page">
      {state === "polling" && (
        <div data-testid="store-status-polling">
          <Loader2 className="mx-auto text-[var(--accent)] animate-spin mb-4" size={40} />
          <h1 className="font-display font-extrabold text-2xl mb-2">Confirming your payment…</h1>
          <p className="text-[var(--ink-2)]">This only takes a moment.</p>
        </div>
      )}

      {state === "paid" && order && (
        <div data-testid="store-status-paid">
          <CheckCircle2 className="mx-auto text-[#1B5E20] mb-4" size={48} />
          <h1 className="font-display font-extrabold text-3xl mb-2">Thank you!</h1>
          <p className="text-[var(--ink-2)] mb-6">Your purchase of <strong>{order.product_title}</strong> is complete. We've also emailed your download link to <strong>{order.buyer_email}</strong>.</p>
          <a href={order.download_url} data-testid="store-download-btn" className="inline-flex items-center justify-center gap-2 w-full bg-[var(--accent)] text-white font-semibold py-4 hover:bg-[var(--accent-hover)] transition-colors">
            <Download size={18} /> Download my file
          </a>
          <p className="text-xs text-[var(--ink-2)] flex items-center justify-center gap-1.5 mt-3"><Clock size={13} /> This link expires in 7 days — please download and save your file now.</p>
          <Link to="/store" className="block mt-6 text-sm font-semibold text-[var(--ink-2)] hover:text-[var(--ink)]">Back to store</Link>
        </div>
      )}

      {state === "failed" && (
        <div data-testid="store-status-failed">
          <XCircle className="mx-auto text-[#B71C1C] mb-4" size={44} />
          <h1 className="font-display font-extrabold text-2xl mb-2">We couldn't confirm your payment yet</h1>
          <p className="text-[var(--ink-2)] mb-6">If you completed payment, your download link has been emailed to you. Otherwise please try again.</p>
          <Link to="/store" className="inline-block bg-[var(--ink)] text-white font-semibold px-6 py-3 hover:bg-[var(--accent)] transition-colors">Back to store</Link>
        </div>
      )}
    </div>
  );
}
